import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  useFragment as useApolloFragment,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  type ExternalTransactionSource,
  encodeTextArray,
  externalTransactionKey,
  extractExternalTransactionRefs,
  filterExternalTransactionsWithinCrmRange,
  getLatestNonFutureSignatureDate,
  hydrateExternalTransactions,
  rankCmaTransactions,
} from '@realadvisor/common-types';
import { InfoIcon, MousePointerClickIcon, SparklesIcon } from 'lucide-react';

import { ConfirmationDialog } from '@modules/_shared/components/ConfirmationDialog';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { REPORT_SELECTION_ACTIONS_ANCHOR } from '@modules/_shared/components/report-editor/preview/previewAnchors';
import type { ReportComponentProps } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getTwoYearsAgo } from '@modules/_shared/utils/dates';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import {
  GET_AUTOMATIC_EXTERNAL_TRANSACTIONS_QUERY,
  GET_AUTOMATIC_TRANSACTIONS_QUERY,
  GET_CURRENT_USER_TEAM_OPT_IN_STATUS,
  GET_SAVED_EXTERNAL_TRANSACTIONS_QUERY,
  REPLACE_CMA_REPORTS_PROPERTY_TRANSACTIONS,
  STEP_TRANSACTIONS_FRAGMENT,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { getEligibleExternalTransactionSource } from '@modules/reports/cma-reports/step-transaction/externalTransactionSources';
import OptOutTransaction from '@modules/reports/cma-reports/step-transaction/OptOutTransaction';
import TransactionInfoDialog from '@modules/reports/cma-reports/step-transaction/TransactionInfoDialog';
import TransactionManualSelectionDialog from '@modules/reports/cma-reports/step-transaction/TransactionManualSelectionDialog';
import TransactionSettings from '@modules/reports/cma-reports/step-transaction/TransactionSettings';
import TransactionsListSection from '@modules/reports/cma-reports/step-transaction/TransactionsListSection';
import type {
  CmaPropertyTransaction,
  ExternalTransactionDetailsRow,
} from '@modules/reports/cma-reports/step-transaction/types';
import { refetchTransactionsUnlessReturned } from '@modules/reports/cma-reports/step-transaction/utils/transactionsRefetch';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';

const StepTransactions: React.FC<ReportComponentProps> = props => {
  const { reportId: cmaReportId } = props;
  const { t } = useLocale();
  // "Show me" on the DVF entry.
  useAutoTour('cma-dvf-transactions');
  const { me } = useAppData();
  const { track } = useAnalytics();
  const [, setSearchParams] = useScopedSearchParams('transaction-selection');
  const [isManualSelectionDialogOpen, setIsManualSelectionDialogOpen] =
    useState(false);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  const { data: cmaReportData, complete: isCmaReportLoaded } =
    useApolloFragment({
      fragment: STEP_TRANSACTIONS_FRAGMENT,
      fragmentName: 'StepTransactions',
      from: {
        __typename: 'cma_reports',
        id: cmaReportId,
      },
    });

  const { data: teamOptInData, loading: teamLoading } = useQuery(
    GET_CURRENT_USER_TEAM_OPT_IN_STATUS,
  );

  const adjustPriceByEvolution = Boolean(
    cmaReportData?.adjust_price_by_evolution,
  );

  const isPropertyTransactionSquareMeterCalculation = Boolean(
    cmaReportData?.is_property_transaction_square_meter_calculation,
  );

  const transactionMapZoomLevel =
    cmaReportData?.transaction_map_zoom_level ?? null;

  const handleCloseManualSelection = useCallback(async () => {
    setSearchParams(new URLSearchParams());
    setIsManualSelectionDialogOpen(false);
  }, [setSearchParams]);

  const handleOpenManualSelection = useCallback(async () => {
    if (!cmaReportData?.property?.id) {
      return;
    }

    const propertyFilters = {
      ...(cmaReportData?.property?.locality_id && {
        locality_place: {
          id: {
            _in: [cmaReportData.property.locality_id],
          },
        },
      }),
      ...(cmaReportData?.property?.property_type?.main_type && {
        property_main_type: {
          _in: [cmaReportData.property.property_type.main_type],
        },
      }),
    };

    const transactionFilter = {
      signature_date: {
        _gte: getTwoYearsAgo().toISOString(),
        _lte: new Date().toISOString(),
      },
      ...propertyFilters,
    };

    const whereClause = {
      transaction: transactionFilter,
    };

    // to avoid race with components mounting and writing their params.
    setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('where', JSON.stringify(whereClause));
        return next;
      });
    }, 0);

    setIsManualSelectionDialogOpen(true);
  }, [cmaReportData, setSearchParams]);

  const [getAutomaticTransactions, { loading: loadingAutomatic }] =
    useLazyQuery(GET_AUTOMATIC_TRANSACTIONS_QUERY, {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    });

  const [
    getAutomaticExternalTransactions,
    { loading: loadingAutomaticExternal },
  ] = useLazyQuery(GET_AUTOMATIC_EXTERNAL_TRANSACTIONS_QUERY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const [saveTransactions, { loading: savingTransactions }] = useMutation(
    REPLACE_CMA_REPORTS_PROPERTY_TRANSACTIONS,
    {
      onError: setErrorLike,
    },
  );

  const propertyTransactions = useMemo<CmaPropertyTransaction[]>(
    () =>
      (cmaReportData?.cma_property_transactions?.filter(
        (t): t is CmaPropertyTransaction => t != null,
      ) ?? []) as CmaPropertyTransaction[],
    [cmaReportData?.cma_property_transactions],
  );

  const externalSourceAdapter = getEligibleExternalTransactionSource({
    countryCode: cmaReportData?.property?.country_code,
    lat: cmaReportData?.property?.lat,
    lng: cmaReportData?.property?.lng,
  });

  const savedExternalRefs = useMemo(
    () => extractExternalTransactionRefs(propertyTransactions),
    [propertyTransactions],
  );
  const savedExternalSource = savedExternalRefs[0]?.source ?? null;

  const [externalDetailsByKey, setExternalDetailsByKey] = useState(
    () => new Map<string, ExternalTransactionDetailsRow>(),
  );

  const savedExternalIds = useMemo(
    () => savedExternalRefs.map(ref => ref.externalId),
    [savedExternalRefs],
  );
  const requestedExternalKeys = useMemo(
    () =>
      new Set(
        savedExternalRefs.map(ref =>
          externalTransactionKey(ref.source, ref.externalId),
        ),
      ),
    [savedExternalRefs],
  );

  const {
    data: savedExternalData,
    error: savedExternalError,
    loading: savedExternalLoading,
  } = useQuery(GET_SAVED_EXTERNAL_TRANSACTIONS_QUERY, {
    variables: {
      source: savedExternalSource ?? '',
      externalIds: savedExternalIds,
    },
    skip: savedExternalSource == null || savedExternalIds.length === 0,
    context: { clientName: 'scrapers' },
  });

  useEffect(() => {
    if (savedExternalError) {
      setErrorLike(savedExternalError);
    }
  }, [savedExternalError]);

  useEffect(() => {
    if (savedExternalLoading) {
      return;
    }

    const nextDetailsByKey = new Map<string, ExternalTransactionDetailsRow>();
    if (!savedExternalError) {
      for (const row of savedExternalData?.fr_external_transactions ?? []) {
        const details = row as ExternalTransactionDetailsRow;
        const key = externalTransactionKey(details.source, details.external_id);
        if (requestedExternalKeys.has(key)) {
          nextDetailsByKey.set(key, details);
        }
      }
    }

    setExternalDetailsByKey(nextDetailsByKey);
  }, [
    requestedExternalKeys,
    savedExternalData,
    savedExternalError,
    savedExternalLoading,
  ]);

  const hydratedPropertyTransactions = useMemo<CmaPropertyTransaction[]>(
    () =>
      hydrateExternalTransactions(propertyTransactions, externalDetailsByKey),
    [propertyTransactions, externalDetailsByKey],
  );

  const isExternalHydrationLoading =
    savedExternalIds.length > 0 && savedExternalLoading;

  const existingRowIds = useMemo(
    () => propertyTransactions.map(t => t.id),
    [propertyTransactions],
  );

  const creationSource =
    (cmaReportData as { report_source?: string })?.report_source ?? null;

  const propertyId = cmaReportData?.property?.id ?? null;
  const propertyLat = cmaReportData?.property?.lat ?? null;
  const propertyLng = cmaReportData?.property?.lng ?? null;
  const propertyMainType =
    cmaReportData?.property?.property_type?.main_type ?? null;
  const propertyValuation =
    cmaReportData?.property?.latest_appraisal?.value ?? null;

  const handleAutomaticSelection = useCallback(async () => {
    if (propertyId == null) {
      console.error('Missing property id');
      return;
    }

    const cutoffDate = getTwoYearsAgo().toISOString().split('T')[0];

    try {
      const maxSignatureDate = new Date().toISOString().slice(0, 10);
      const externalResult =
        externalSourceAdapter && propertyLat != null && propertyLng != null
          ? await getAutomaticExternalTransactions({
              variables: {
                lat: propertyLat,
                lng: propertyLng,
                source: encodeTextArray([externalSourceAdapter.source]),
                property_main_type: encodeTextArray(
                  propertyMainType ? [propertyMainType] : null,
                ),
                cutoff_date: cutoffDate,
                cutoff_date_max: maxSignatureDate,
              },
              context: { clientName: 'scrapers' },
            }).catch(err => {
              console.error('External transaction search failed:', err);
              return { data: null };
            })
          : { data: null };
      const externalRows =
        externalResult.data?.fr_get_external_transactions_distance?.filter(
          row =>
            row.signature_date == null ||
            row.signature_date.slice(0, 10) <= maxSignatureDate,
        ) ?? [];

      const uncappedCrmResult = await getAutomaticTransactions({
        variables: {
          target_property_id: propertyId,
          max_signature_date: null,
        },
      });
      const uncappedCrmRows =
        uncappedCrmResult.data?.get_property_transactions_distance;
      if (uncappedCrmResult.error != null || uncappedCrmRows == null) {
        throw (
          uncappedCrmResult.error ??
          new Error('Automatic CRM transaction search returned no data')
        );
      }
      const boundedExternalRows = filterExternalTransactionsWithinCrmRange(
        uncappedCrmRows,
        externalRows,
        externalSourceAdapter ? propertyValuation : null,
      );
      const externalLatestSignatureDate = getLatestNonFutureSignatureDate(
        boundedExternalRows,
        maxSignatureDate,
      );

      let crmRows = uncappedCrmRows;
      if (externalLatestSignatureDate != null) {
        const cappedCrmResult = await getAutomaticTransactions({
          variables: {
            target_property_id: propertyId,
            max_signature_date: externalLatestSignatureDate,
          },
        });
        const cappedCrmRows =
          cappedCrmResult.data?.get_property_transactions_distance;
        if (cappedCrmResult.error != null || cappedCrmRows == null) {
          throw (
            cappedCrmResult.error ??
            new Error(
              'Capped automatic CRM transaction search returned no data',
            )
          );
        }
        crmRows = cappedCrmRows;
      }
      const usableExternalRows =
        externalLatestSignatureDate == null ? [] : boundedExternalRows;

      const crmOnlyPool = rankCmaTransactions(
        uncappedCrmRows,
        [],
        externalSourceAdapter ? propertyValuation : null,
      );
      let pool = rankCmaTransactions(
        crmRows,
        usableExternalRows,
        externalSourceAdapter ? propertyValuation : null,
      );
      if (pool.length < 10 && crmOnlyPool.length > pool.length) {
        pool = crmOnlyPool;
      }

      if (pool.length === 0 && existingRowIds.length === 0) {
        return;
      }

      const transactionsToSave = pool.map((entry, index) =>
        entry.kind === 'crm'
          ? {
              cma_report_id: cmaReportId,
              source: 'crm' as const,
              transaction_id: entry.row.transaction_id,
              order_nr: index,
              distance_in_meters: entry.row.distance_in_meters,
            }
          : {
              cma_report_id: cmaReportId,
              source: entry.row.source as ExternalTransactionSource,
              external_id: entry.row.external_id,
              order_nr: index,
              distance_in_meters: entry.row.distance_in_meters,
              include_in_median: true,
            },
      );

      const automaticExternalDetails: ExternalTransactionDetailsRow[] = [];
      for (const entry of pool) {
        if (entry.kind !== 'external') {
          continue;
        }
        automaticExternalDetails.push(
          entry.row as ExternalTransactionDetailsRow,
        );
      }
      if (automaticExternalDetails.length > 0) {
        setExternalDetailsByKey(currentDetails => {
          const nextDetails = new Map(currentDetails);
          for (const details of automaticExternalDetails) {
            nextDetails.set(
              externalTransactionKey(details.source, details.external_id),
              details,
            );
          }
          return nextDetails;
        });
      }

      await saveTransactions({
        variables: {
          cma_report_id: cmaReportId,
          transactions: transactionsToSave,
          crm_transaction_ids: pool.flatMap(entry =>
            entry.kind === 'crm' ? [entry.row.transaction_id] : [],
          ),
          external_transaction_ids: pool.flatMap(entry =>
            entry.kind === 'external' ? [entry.row.external_id] : [],
          ),
          to_delete_ids: existingRowIds,
        },
        refetchQueries: refetchTransactionsUnlessReturned(cmaReportId),
        awaitRefetchQueries: true,
      });

      const baseProperties = getBaseCmaEventProperties(me, cmaReportId);
      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_TRANSACTIONS_AUTO_SEARCH, {
          ...baseProperties,
          count: transactionsToSave.length,
          creation_source: creationSource,
        });
      }
    } catch (error) {
      console.error('Failed to automatically select transactions:', error);
    }
  }, [
    propertyId,
    propertyLat,
    propertyLng,
    externalSourceAdapter,
    getAutomaticTransactions,
    getAutomaticExternalTransactions,
    propertyMainType,
    propertyValuation,
    saveTransactions,
    cmaReportId,
    existingRowIds,
    me,
    track,
    creationSource,
  ]);

  const handleAutomaticButtonClick = useCallback(() => {
    if (propertyTransactions.length > 0) {
      setConfirmationDialogOpen(true);
    } else {
      handleAutomaticSelection();
    }
  }, [propertyTransactions.length, handleAutomaticSelection]);

  const teamData = teamOptInData?.get_me?.me?.default_team;
  const userTeams = teamOptInData?.get_me?.me?.teams_users;

  // Check if user is a team leader of their default team
  const isTeamLeader = useMemo(() => {
    if (!teamData?.id || !userTeams) {
      return false;
    }
    const foundTeam = userTeams.find(team => team.team_id === teamData.id);
    return foundTeam?.is_leader ?? false;
  }, [teamData?.id, userTeams]);

  if (!isCmaReportLoaded || teamLoading) {
    return <LoadingSpinner />;
  }

  if (teamData?.share_transactions_in_cma === false) {
    return <OptOutTransaction />;
  }

  return (
    <>
      <TransactionManualSelectionDialog
        open={isManualSelectionDialogOpen}
        onClose={handleCloseManualSelection}
        cmaReportId={cmaReportId}
        existingTransactions={hydratedPropertyTransactions}
      />
      <Box
        sx={{
          flexGrow: 1,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Stack
          sx={{
            gap: 1,
            p: 2,
            flexGrow: 1,
            height: 0,
            overflow: 'auto',
          }}
        >
          <TransactionSettings
            adjustPriceByEvolution={adjustPriceByEvolution}
            isPropertyTransactionSquareMeterCalculation={
              isPropertyTransactionSquareMeterCalculation
            }
            transactionMapZoomLevel={transactionMapZoomLevel}
          />

          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Stack
              {...tourAnchor(REPORT_SELECTION_ACTIONS_ANCHOR)}
              direction="row"
              sx={{
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                startIcon={<SparklesIcon />}
                fullWidth
                variant="contained"
                onClick={handleAutomaticButtonClick}
                loading={loadingAutomatic || loadingAutomaticExternal}
                loadingPosition="start"
              >
                {t('Automatically')}
              </Button>
              <Button
                startIcon={<MousePointerClickIcon />}
                onClick={handleOpenManualSelection}
                variant="outlined"
                fullWidth
                disabled={
                  loadingAutomatic ||
                  loadingAutomaticExternal ||
                  savingTransactions
                }
              >
                {t('Manually')}
              </Button>
            </Stack>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                {t('Choose transactions')}
              </Typography>
              {isTeamLeader && (
                <InfoIcon
                  onClick={() => setInfoDialogOpen(true)}
                  style={{ cursor: 'pointer' }}
                  size={18}
                  color="var(--mui-palette-primary-main)"
                />
              )}
            </Stack>
          </Stack>

          <TransactionsListSection
            propertyTransactions={hydratedPropertyTransactions}
            cmaReportId={cmaReportId}
            adjustPriceByEvolution={adjustPriceByEvolution}
            isExternalHydrationLoading={isExternalHydrationLoading}
          />
        </Stack>

        <FooterActionsComponentWithoutForm
          updating={false}
          step={props.step}
          setStep={props.setStep}
        />
      </Box>
      <ConfirmationDialog
        title={t('Refresh Transactions')}
        message={t(
          'This action will refresh the current list of transactions. All selected transactions will be replaced.',
        )}
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={() => {
          setConfirmationDialogOpen(false);
          handleAutomaticSelection();
        }}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
      <TransactionInfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        teamId={teamOptInData?.get_me?.me?.default_team?.id}
      />
    </>
  );
};

export default StepTransactions;
